import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { validateStnr } from "@/utils/lexSteuernummer";
import { applyUvaField } from "@/components/lex/LexUvaYearPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Handlungsrelevante Hinweise bleiben sichtbar, Rechenwege wandern in „Berechnungsdetails".
const isKeyWarning = (w) => /Keine Vorsteuerpauschalierung|Höchstbetrag|keine Vorsteuerpauschalierung mehr/.test(w);
const fmtEur = (v) => `€ ${Number(v || 0).toLocaleString("de-AT", { minimumFractionDigits: 2 })}`;
const prevPeriod = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const prevQuarter = () => {
  const d = new Date();
  let q = Math.floor(d.getMonth() / 3);
  let y = d.getFullYear();
  if (q === 0) { q = 4; y -= 1; }
  return `${y}-Q${q}`;
};
const MONTHS = ["Jänner", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const lastMonths = (n = 18) => {
  const out = [];
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  for (let i = 0; i < n; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};
const withCurrent = (list, p) => (p && !list.includes(p) ? [p, ...list] : list);
const lastQuarters = (n = 6) => {
  const out = [];
  let [y, q] = prevQuarter().split("-Q").map(Number);
  for (let i = 0; i < n; i++) {
    out.push(`${y}-Q${q}`);
    q -= 1;
    if (q === 0) { q = 4; y -= 1; }
  }
  return out;
};

// Kanzlei: One-Click-UVA — Vorschau berechnen, prüfen, freigeben (ersetzt die Perioden-Review für lex)
// Manuelle Korrektur vor dem Versand (Entwurf) — gleiche Endpunkte wie die Jahresübersicht.
const UvaOverrideEditor = ({ clientId, period, data, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const n = (v) => { const x = parseFloat(String(v ?? "").replace(",", ".")); return Number.isFinite(x) ? x : null; };
  const start = () => {
    setForm({ basis_netto: data.basis_netto, ust: data.ust, vstp: data.vstp, note: "" });
    setOpen(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/lex/advisor/uva/${clientId}/${period}/draft`, {
        basis_netto: n(form.basis_netto), ust: n(form.ust), vstp: n(form.vstp), note: form.note || null,
      });
      toast.success("Werte als Entwurf gespeichert — bitte prüfen und dann freigeben.");
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/lex/advisor/uva/${clientId}/${period}/draft`);
      toast.success("Auf automatische Berechnung zurückgesetzt.");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Zurücksetzen fehlgeschlagen.");
    } finally { setBusy(false); }
  };
  if (!open) {
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" onClick={start}
          className="text-xs uppercase tracking-wider font-semibold px-3 py-2 border border-slate-200 text-slate-600 hover:border-[#C5A059] hover:text-[#0A1128] transition-colors"
          data-testid="lex-uva-drawer-edit-toggle">Werte manuell anpassen</button>
        {data.override && (
          <button type="button" onClick={reset} disabled={busy}
            className="text-xs uppercase tracking-wider font-semibold px-3 py-2 border border-slate-200 text-slate-500 hover:border-[#C5A059] transition-colors disabled:opacity-50"
            data-testid="lex-uva-drawer-reset">Auf Automatik zurücksetzen</button>
        )}
      </div>
    );
  }
  const field = (key, label) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
      <input type="number" step="0.01" value={form[key]} onChange={(e) => setForm(applyUvaField(form, key, e.target.value))}
        className="w-full border border-slate-200 px-2 py-1.5 text-sm lex-mono focus:outline-none focus:border-[#C5A059]"
        data-testid={`lex-uva-drawer-edit-${key}`} />
      <span className="text-[10px] text-slate-400 lex-mono">Automatik: {fmtEur(data.auto?.[key])}</span>
    </label>
  );
  return (
    <div className="border p-3 space-y-3" style={{ borderColor: "#C5A059", background: "#FDFCFB" }} data-testid="lex-uva-drawer-editor">
      <div className="grid grid-cols-3 gap-2">
        {field("basis_netto", "Einnahmen netto")}
        {field("ust", "Umsatzsteuer")}
        {field("vstp", "Vorsteuer")}
      </div>
      <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={300}
        placeholder="Grund der Änderung (intern, im Protokoll sichtbar)"
        className="w-full border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:border-[#C5A059]"
        data-testid="lex-uva-drawer-edit-note" />
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-600">Neue Zahllast: <span className="lex-mono font-semibold" data-testid="lex-uva-drawer-edit-zahllast">{fmtEur((n(form.ust) || 0) - (n(form.vstp) || 0))}</span></span>
        <div className="flex gap-2">
          <button type="button" onClick={() => setOpen(false)} className="text-xs uppercase tracking-wider text-slate-500 px-3 py-1.5"
            data-testid="lex-uva-drawer-edit-cancel">Abbrechen</button>
          <button type="button" onClick={save} disabled={busy}
            className="text-xs uppercase tracking-wider font-semibold px-3 py-1.5 disabled:opacity-50"
            style={{ background: "#0A1128", color: "#C5A059" }} data-testid="lex-uva-drawer-edit-save">
            {busy ? "Speichert …" : "Als Entwurf speichern"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const LexUvaDrawer = ({ client, onClose, onDone, initialPeriod = null }) => {
  const [period, setPeriod] = useState(initialPeriod || prevPeriod());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [freq, setFreq] = useState(null);
  const [stnrInput, setStnrInput] = useState("");
  const [stnrBusy, setStnrBusy] = useState(false);
  const [showExt, setShowExt] = useState(false);
  const [extNote, setExtNote] = useState("");

  useEffect(() => {
    setData(null);
    axios.get(`${API}/lex/advisor/uva/${client.client_id}/${period}`)
      .then((res) => {
        setData(res.data);
        // Erstes Laden: gespeicherten UVA-Rhythmus übernehmen (Quartals-Mandanten → Quartalsauswahl)
        setFreq((f) => {
          if (f === null && !initialPeriod && res.data.vat_filing_frequency === "quarterly") {
            setPeriod(prevQuarter());
            return "quarterly";
          }
          return f === null ? res.data.vat_filing_frequency : f;
        });
      })
      .catch(() => toast.error("Vorschau konnte nicht geladen werden."));
  }, [client.client_id, period, initialPeriod]);

  const approve = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/lex/advisor/uva/${client.client_id}/${period}/approve`);
      toast.success(`UVA ${period} freigegeben — der Anwalt erhält die Zahlungsanweisung per App & Mail.`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Freigabe fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  // FinanzOnline-Übermittlung je Periode vermerken (Übermittelt-Haken)
  const toggleFo = async (submitted) => {
    try {
      const res = await axios.post(`${API}/lex/advisor/uva/${client.client_id}/${period}/finanzonline`, { submitted });
      setData((d) => ({ ...d, existing_run: { ...d.existing_run, fo_submitted_at: res.data.fo_submitted_at } }));
      toast.success(submitted ? "FinanzOnline-Übermittlung vermerkt." : "Vermerk entfernt.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte nicht gespeichert werden.");
    }
  };

  // U30-XML für den FinanzOnline-Upload herunterladen
  const downloadXml = async () => {
    try {
      const res = await axios.get(`${API}/lex/advisor/uva/${client.client_id}/${period}/xml`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `UVA_U30_${client.name || "Mandant"}_${period}.xml`.replace(/\s+/g, "_");
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("XML konnte nicht erzeugt werden.");
    }
  };

  const reload = () => axios.get(`${API}/lex/advisor/uva/${client.client_id}/${period}`)
    .then((res) => setData(res.data))
    .catch(() => toast.error("Vorschau konnte nicht geladen werden."));

  const done = data?.existing_run && ["approved", "paid"].includes(data.existing_run.status);
  const external = data?.existing_run?.status === "external";
  const stnrMissing = Boolean(data) && !data.finanzamt_iban;

  // Periode als außerhalb des Systems erledigt markieren (keine Mails, kein Tracking)
  const reloadPreview = async () => {
    const res = await axios.get(`${API}/lex/advisor/uva/${client.client_id}/${period}`);
    setData(res.data);
  };
  const markExternal = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/lex/advisor/uva/${client.client_id}/${period}/external`, { note: extNote });
      toast.success(`UVA ${period} als extern erledigt markiert — keine Mails, kein Zahlungs-Tracking.`);
      setShowExt(false);
      setExtNote("");
      await reloadPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Markierung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };
  const undoExternal = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/lex/advisor/uva/${client.client_id}/${period}/external`);
      toast.success("Markierung aufgehoben — die Periode ist wieder offen.");
      await reloadPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aufheben fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  // Steuernummer direkt aus dem Drawer nachtragen — danach Vorschau neu laden
  const saveStnr = async () => {
    const err = validateStnr(stnrInput);
    if (err) { toast.error(err); return; }
    setStnrBusy(true);
    try {
      await axios.post(`${API}/lex/advisor/clients/${client.client_id}/steuernummer`, { steuernummer: stnrInput });
      toast.success("Steuernummer gespeichert.");
      setStnrInput("");
      const res = await axios.get(`${API}/lex/advisor/uva/${client.client_id}/${period}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setStnrBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="lex-uva-drawer">
      <div className="bg-white max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="lex-gold-line mb-3" />
            <h2 className="lex-serif text-2xl font-semibold" style={{ color: "#0A1128" }}>UVA für {client.anrede || client.name}.</h2>
          </div>
          <button onClick={onClose} className="text-xs uppercase tracking-widest font-semibold text-slate-400 hover:text-slate-600"
            data-testid="lex-uva-drawer-close">Schließen ×</button>
        </div>
        <div className="mt-4 flex items-end gap-4 flex-wrap">
          <div>
            <label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Periode</label>
            {freq === "quarterly" ? (
              <select value={period} onChange={(e) => setPeriod(e.target.value)}
                className="block mt-1 border border-slate-200 px-3 py-2 text-sm lex-mono bg-white"
                data-testid="lex-uva-quarter-select">
                {withCurrent(lastQuarters(6), period).map((p) => (
                  <option key={p} value={p}>{`Q${p.split("-Q")[1]}/${p.slice(0, 4)}`}</option>
                ))}
              </select>
            ) : (
              <select value={period} onChange={(e) => setPeriod(e.target.value)}
                className="block mt-1 border border-slate-200 px-3 py-2 text-sm lex-mono bg-white"
                data-testid="lex-uva-month-select">
                {withCurrent(lastMonths(18), period).map((p) => (
                  <option key={p} value={p}>{`${MONTHS[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}`}</option>
                ))}
              </select>
            )}
          </div>
          {freq && (
            <div data-testid="lex-uva-frequency-info">
              <label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Rhythmus</label>
              <p className="mt-1 px-3 py-2 text-xs border border-slate-200 bg-slate-50 text-slate-600" data-testid="lex-uva-frequency-value">
                {freq === "monthly" ? "Monatlich" : "Quartalsweise"} · laut Stammdaten
              </p>
            </div>
          )}
          {data && (
            <div data-testid="lex-uva-tax-scheme-info">
              <label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Besteuerung</label>
              <p className="mt-1 px-3 py-2 text-xs border border-slate-200 bg-slate-50 text-slate-600" data-testid="lex-uva-tax-scheme-value">
                {data.tax_scheme === "accrual" ? "Soll (Rechnungsdatum)" : "Ist (vereinnahmt)"} · laut Stammdaten
              </p>
            </div>
          )}
        </div>
        {data && (
          <div className="mt-5 space-y-2 text-sm" data-testid="lex-uva-preview">
            {stnrMissing && !done && (
              <div className="border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="lex-uva-stnr-missing">
                <p className="text-xs font-medium text-amber-800">
                  {data.steuernummer
                    ? `Die hinterlegte Steuernummer „${data.steuernummer}“ ist ungültig — Finanzamt-IBAN nicht ableitbar.`
                    : "Steuernummer fehlt — ohne sie können Finanzamt-IBAN und QR-Code nicht erzeugt werden."} Bitte nachtragen:
                </p>
                <div className="flex gap-2">
                  <input className="flex-1 border border-amber-300 px-3 py-2 text-sm lex-mono bg-white"
                    placeholder="99 999/9999" value={stnrInput} onChange={(e) => setStnrInput(e.target.value)}
                    data-testid="lex-uva-stnr-input" />
                  <Button onClick={saveStnr} disabled={stnrBusy}
                    className="rounded-none text-xs uppercase tracking-wider"
                    style={{ background: "#0A1128", color: "#C5A059" }} data-testid="lex-uva-stnr-save-btn">
                    {stnrBusy ? "Speichert …" : "Speichern"}
                  </Button>
                </div>
              </div>
            )}
            <div className="flex justify-between border-t border-slate-100 pt-2"><span className="text-slate-500">{data.tax_scheme === "accrual" ? "Fakturierte Honorare (netto)" : "Vereinnahmte Honorare (netto)"}</span><span className="lex-mono">{fmtEur(data.basis_netto)}</span></div>
            <div className="flex justify-between border-t border-slate-100 pt-2"><span className="text-slate-500">Umsatzsteuer 20 %</span><span className="lex-mono">{fmtEur(data.ust)}</span></div>
            <div className="flex justify-between border-t border-slate-100 pt-2"><span className="text-slate-500">{data.override ? "− Vorsteuer" : "− Vorsteuerpauschale 1,8 %"}</span><span className="lex-mono">−{fmtEur(data.vstp)}</span></div>
            <div className="flex justify-between border-t-2 pt-2 font-semibold" style={{ borderColor: "#C5A059" }}>
              <span>Zahllast</span><span className="lex-mono" data-testid="lex-uva-zahllast">{fmtEur(data.zahllast)}</span>
            </div>
            {data.override && (
              <p className="text-[11px] p-2 border" style={{ borderColor: "#C5A059", background: "#FFFBEB", color: "#92650B" }}
                data-testid="lex-uva-override-hint">
                Manuell angepasst (Entwurf){data.override_by ? ` von ${data.override_by}` : ""} · Automatik: Zahllast {fmtEur(data.auto?.zahllast)}
              </p>
            )}
            {!done && !external && (
              <UvaOverrideEditor clientId={client.client_id} period={period} data={data} onChanged={reload} />
            )}
            <p className="text-xs text-slate-400">Fällig am {new Date(data.due_date).toLocaleDateString("de-AT")} <span data-testid="lex-uva-basis-count"> · Basis: {data.paid_invoice_count} {data.tax_scheme === "accrual" ? "ausgestellte" : "bezahlte"} Honorarnote(n){data.manual_income_count ? ` + ${data.manual_income_count} manuelle Einnahme(n)` : ""}</span></p>
            {(data.warnings || []).filter(isKeyWarning).map((w, i) => (
              <p key={`k${i}`} className="text-xs font-medium text-amber-800 bg-amber-50 p-2" data-testid={`lex-uva-key-warning-${i}`}>{w}</p>
            ))}
            {(data.warnings || []).some((w) => !isKeyWarning(w)) && (
              <details className="text-[11px] text-slate-500" data-testid="lex-uva-details">
                <summary className="cursor-pointer select-none uppercase tracking-wider font-semibold text-slate-400 py-1"
                  data-testid="lex-uva-details-toggle">Berechnungsdetails</summary>
                <div className="space-y-1 mt-1">
                  {(data.warnings || []).filter((w) => !isKeyWarning(w)).map((w, i) => (
                    <p key={i} className="bg-slate-50 p-2" data-testid={`lex-uva-warning-${i}`}>{w}</p>
                  ))}
                </div>
              </details>
            )}
            {data.open_invoice_count > 0 && !data.honorar_paid_optout && data.tax_scheme !== "accrual" && (
              <p className="text-xs font-medium text-amber-700 bg-amber-50 p-2" data-testid="lex-uva-open-warning">
                ⚠ {data.open_invoice_count} Honorarnote(n) ohne Bezahldatum — Ist-Besteuerung: nur bezahlte Noten zählen in die UVA. Bitte vor Freigabe klären.
              </p>
            )}
            {data.tax_scheme !== "accrual" && data.honorar_paid_optout ? (
              <p className="text-xs text-slate-500 bg-slate-50 p-2" data-testid="lex-uva-optout-hint">
                Automatische Bezahlt-Verbuchung aktiv: Noten gelten zum Monatsende als bezahlt —
                vom Anwalt in seinen Honorar-Einstellungen aktiviert.
              </p>
            ) : null}
            {/* Sichtprüfung Zahlungsempfänger (Sep 2026): menschliche Kontrolle vor jeder Freigabe */}
            {data.finanzamt_dienststelle && (
              <div className="border p-3 mt-1" style={{ borderColor: "#C5A059", background: "#FBF7EF" }}
                data-testid="lex-uva-empfaenger-check">
                <p className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#8A6D3B" }}>
                  Sichtprüfung Zahlungsempfänger
                </p>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Dienststelle</span>
                    <span className="font-medium text-right" data-testid="lex-uva-dienststelle-name">
                      {data.finanzamt_dienststelle.name} (Nr. {data.finanzamt_dienststelle.nr})
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Ziel-IBAN</span>
                    <span className="lex-mono" data-testid="lex-uva-ziel-iban">
                      {(data.finanzamt_dienststelle.iban || "").replace(/(.{4})/g, "$1 ").trim()}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Abgeleitet aus StNr</span>
                    <span className="lex-mono">{data.steuernummer}</span>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  Bitte vor der Freigabe prüfen, ob die Dienststelle zur Steuernummer des Mandanten passt —
                  der Anwalt überweist per QR-Code exakt an diese IBAN (BIC BUNDATWW).
                </p>
              </div>
            )}
          </div>
        )}
        {done && (
          <p className="mt-4 text-[11px] text-slate-500 border border-slate-200 bg-slate-50 p-2 lex-mono"
            data-testid="lex-uva-audit-line">
            Protokoll: Freigegeben von {data.existing_run.approved_by_name || data.existing_run.approved_by_email || "—"}
            {data.existing_run.approved_at ? ` am ${new Date(data.existing_run.approved_at).toLocaleString("de-AT", { dateStyle: "short", timeStyle: "short" })}` : ""} · IBAN {(data.existing_run.audit?.iban_shown || data.existing_run.iban || "").replace(/(.{4})/g, "$1 ").trim()}
          </p>
        )}
        <div className="mt-6 flex gap-2">
          <Button onClick={approve} disabled={busy || !data || done || external || stnrMissing}
            className="rounded-none flex-1 text-xs uppercase tracking-wider"
            style={{ background: "#0A1128", color: "#C5A059" }} data-testid="lex-uva-approve-btn">
            {done ? `Bereits freigegeben (${data.existing_run.status === "paid" ? "bezahlt" : "offen"})`
              : external ? "Als extern erledigt markiert"
              : stnrMissing ? "Steuernummer erforderlich"
              : busy ? "Wird freigegeben …" : "Freigeben & Anwalt informieren"}
          </Button>
          <Button onClick={downloadXml} disabled={!data || stnrMissing} variant="outline"
            className="rounded-none text-xs uppercase tracking-wider border-slate-300"
            title="U30-XML für den FinanzOnline-Datei-Upload" data-testid="lex-uva-xml-btn">
            U30-XML
          </Button>
        </div>
        {data && !stnrMissing && (
          <p className="text-[11px] text-slate-400 mt-2">
            U30-XML: fertige Übermittlungsdatei für den FinanzOnline-Upload (Eingaben → Übermittlung → Datei hochladen).
          </p>
        )}
        {/* Extern erledigt (Sep 2026): UVA wurde außerhalb von lex.ivoliq eingereicht */}
        {external && (
          <div className="mt-3 border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
            data-testid="lex-uva-external-banner">
            <span className="font-semibold">Extern erledigt</span>
            {" — markiert von "}{data.existing_run.approved_by_name || data.existing_run.approved_by_email || "—"}
            {data.existing_run.approved_at ? ` am ${new Date(data.existing_run.approved_at).toLocaleDateString("de-AT")}` : ""}
            {data.existing_run.external_note ? <span className="block text-slate-500 mt-1">Notiz: „{data.existing_run.external_note}“</span> : null}
            <button onClick={undoExternal} disabled={busy}
              className="block mt-2 underline text-slate-500 hover:text-slate-700"
              data-testid="lex-uva-external-undo">Markierung aufheben</button>
          </div>
        )}
        {data && !done && !external && (
          <div className="mt-3 border-t border-slate-100 pt-3" data-testid="lex-uva-external-block">
            {!showExt ? (
              <button onClick={() => setShowExt(true)}
                className="text-xs uppercase tracking-wider font-semibold text-slate-400 hover:text-slate-600"
                data-testid="lex-uva-external-open">
                Extern erledigt? (außerhalb des Systems eingereicht) →
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">
                  Markiert die Periode als außerhalb von lex.ivoliq erledigt (z. B. direkt in FinanzOnline
                  oder über das Alt-System). Es werden <span className="font-semibold">keine Mails</span> versendet
                  und keine Zahlung getrackt. Rückgängig machbar.
                </p>
                <input value={extNote} onChange={(e) => setExtNote(e.target.value)} maxLength={300}
                  placeholder="Notiz (optional), z. B. „direkt in FinanzOnline eingereicht“"
                  className="w-full border border-slate-200 px-3 py-2 text-sm" data-testid="lex-uva-external-note" />
                <div className="flex gap-2 items-center">
                  <Button onClick={markExternal} disabled={busy} variant="outline"
                    className="rounded-none text-xs uppercase tracking-wider border-slate-300"
                    data-testid="lex-uva-external-confirm">
                    {busy ? "Speichert …" : "Als extern erledigt markieren"}
                  </Button>
                  <button onClick={() => setShowExt(false)} className="text-xs text-slate-400 hover:text-slate-600"
                    data-testid="lex-uva-external-cancel">Abbrechen</button>
                </div>
              </div>
            )}
          </div>
        )}
        {done && (
          <label className="mt-3 flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="lex-uva-fo-label">
            <input type="checkbox" checked={Boolean(data.existing_run?.fo_submitted_at)}
              onChange={(e) => toggleFo(e.target.checked)} className="mt-0.5" data-testid="lex-uva-fo-checkbox" />
            <span>
              <span className="font-semibold">U30-XML bei FinanzOnline hochgeladen.</span>
              {data.existing_run?.fo_submitted_at && (
                <span className="block text-slate-400" data-testid="lex-uva-fo-date">
                  Vermerkt am {new Date(data.existing_run.fo_submitted_at).toLocaleDateString("de-AT")}
                  {data.existing_run.fo_submitted_by ? ` · ${data.existing_run.fo_submitted_by}` : ""}
                </span>
              )}
            </span>
          </label>
        )}
      </div>
    </div>
  );
};

export default LexUvaDrawer;
